/**
 * Subtle industry-aware accent colors for badges, focus hints, links — never full-theme overrides.
 * Aligns with `tenants.industry_type` / AuthContext `industryType`.
 * @module
 */

import { foundation } from './tokens';
import { semantic } from './theme/semanticTokens';

const { color } = foundation;

/** Default: brand navy — calm, premium base */
const DEFAULT_ACCENT = {
  key: 'default',
  accent: color.brand.navy700,
  accentSoft: color.neutral.surfaceMuted,
  borderSoft: color.neutral.border,
  focusRing: semantic.industry.defaultFocus,
};

/**
 * Muted accent pairs — use for borders, soft fills, inline highlights only.
 * @type {Record<string, { key: string, accent: string, accentSoft: string, borderSoft: string, focusRing: string }>}
 */
const INDUSTRY_ACCENTS = {
  pool: {
    key: 'pool',
    accent: semantic.industry.poolAccent,
    accentSoft: semantic.industry.poolSoft,
    borderSoft: semantic.industry.poolBorder,
    focusRing: semantic.industry.poolFocus,
  },
  hvac: {
    key: 'hvac',
    accent: semantic.industry.hvacAccent,
    accentSoft: semantic.industry.hvacSoft,
    borderSoft: semantic.industry.hvacBorder,
    focusRing: semantic.industry.hvacFocus,
  },
  cleaning: {
    key: 'cleaning',
    accent: semantic.industry.cleaningAccent,
    accentSoft: semantic.industry.cleaningSoft,
    borderSoft: semantic.industry.cleaningBorder,
    focusRing: semantic.industry.cleaningFocus,
  },
  landscaping: {
    key: 'landscaping',
    accent: semantic.industry.landscapingAccent,
    accentSoft: semantic.industry.landscapingSoft,
    borderSoft: semantic.industry.landscapingBorder,
    focusRing: semantic.industry.landscapingFocus,
  },
  pest_control: {
    key: 'pest_control',
    accent: semantic.industry.pestAccent,
    accentSoft: semantic.industry.pestSoft,
    borderSoft: semantic.industry.pestBorder,
    focusRing: semantic.industry.pestFocus,
  },
  generic: {
    key: 'generic',
    accent: color.brand.navy600,
    accentSoft: color.neutral.surfaceMuted,
    borderSoft: color.neutral.borderStrong,
    focusRing: semantic.industry.genericFocus,
  },
  other: {
    key: 'other',
    accent: semantic.industry.otherAccent,
    accentSoft: semantic.industry.otherSoft,
    borderSoft: semantic.industry.otherBorder,
    focusRing: semantic.industry.otherFocus,
  },
};

/**
 * @param {string | null | undefined} industryType — e.g. AuthContext `industryType`
 * @returns {{ key: string, accent: string, accentSoft: string, borderSoft: string, focusRing: string }}
 */
export function getIndustryAccent(industryType) {
  if (industryType == null || industryType === '') {
    return INDUSTRY_ACCENTS.generic ?? DEFAULT_ACCENT;
  }
  return INDUSTRY_ACCENTS[industryType] ?? DEFAULT_ACCENT;
}
